
import { Component, Vue, Watch, Ref } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import MatrixContainer from "@/components/MatrixContainer.vue"; // TODO: encapsulate AlarmClip inside Camera
import DeviceController from "@/components/DeviceController.vue";
import { CameraType, ClipType, NearbyCameraType, SearchedCamera } from "@/store/site-monitor-cameras/types";
import { EventDetails } from "@/store/site-monitor/types";
import { axiosInstance } from "@/axios.instance";
import organizationApi from "@/services/api.organizationSettings";
import { SessionResource } from "@/store/sessions/types";
import ZendeskHelp from "./ZendeskHelp.vue";
import Matrix from "@/types/sv-data/media-matrix/Matrix";
import { MatrixLayoutIndices, MediaMatrixSystemDefaults, SystemDefaultMediaMatrixLayout } from "@/types/sv-data/media-matrix/Constants";
import { MediaMatrixLayout } from "@/types/organization-settings/MediaMatrixLayout";
import { Modules } from "@/store/ModuleTypes";

const SiteMonitor = namespace(Modules.SiteMonitor);
const SMCameras = namespace(Modules.SiteMonitorCameras);
const SessionStore = namespace(Modules.Sessions);
const Tours = namespace(Modules.Tours);

@Component({
	components: {
		"matrix-container": MatrixContainer,
		"device-controller": DeviceController,
		"zendeskHelp" : ZendeskHelp,
	}
})
export default class Cameras extends Vue {
	@Ref() private expandedCamera: HTMLDivElement;
	@Ref() private deviceController: DeviceController;

	@Getter getFeature: (featureName: string[]) => boolean;

	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
	@SiteMonitor.Getter("getIsController") isController: boolean;
	@SiteMonitor.Getter("getIsCameraEnabled") isCameraEnabled: (deviceId: number) => boolean;
	
	@SiteMonitor.Action private fetchDeviceServerDetails: (deviceIds: number[]) => Promise<void>;
	@SiteMonitor.Action createAuditRecord: any;

	@SMCameras.Getter("getLayouts") layouts!: number[][];
	@SMCameras.Getter("getLayoutIndex") getLayoutIndex!: number;		
	@SMCameras.Getter("getAwaitingCamera") awaitingCamera: CameraType | NearbyCameraType | SearchedCamera;
	@SMCameras.Getter("getAwaitingClip") awaitingClip: ClipType;
	@SMCameras.Getter("getUserLastLayoutIndex") userLastLayoutIndex: number | null;
	@SMCameras.Getter("getDeviceControllerCameras") cameras!: any[];
	@SMCameras.Getter("getDeviceControllerClips") clips!: ClipType[];
	@SMCameras.Getter("getAreaCameras") areaCameras!: CameraType[];
	@SMCameras.Getter("getLinkRecord") linkRecord: any;
	@SMCameras.Getter getMatrixPushContents: any;
	@SMCameras.Getter getMatrixContents: any;
	@SMCameras.Getter getAllMatrixContents: any;

	@SMCameras.Action saveMatrix: (matrixData: Matrix) => Promise<void>;
	@SMCameras.Action fetchMatrixForEvent: (eventID: number) => Promise<Matrix>;

	@SMCameras.Mutation clearMatrixContents: any;
	@SMCameras.Mutation setRequiresEventDetails: any;
	@SMCameras.Mutation setMediaMatrixIsNew: any;
	@SMCameras.Mutation setFetchingMatrix: any;
	@SMCameras.Mutation setPushContents: any;
	@SMCameras.Mutation setLayoutIndex: (index: number) => void;
	@SMCameras.Mutation setShownAuditClips: any;
	@SMCameras.Mutation setDeviceControllerCameras: (cameras: any[]) => void;
	@SMCameras.Mutation setDeviceControllerClips: (clips: any[]) => void;
	@SMCameras.Mutation setDeviceControllerOutputs: (outputs: any[]) => void;
	@SMCameras.Mutation setDeviceControllerAudioDevices: (audioDevices: any[]) => void;
	@SMCameras.Mutation setUserLastLayoutIndex: (layoutIndex: number | null) => void;
	@SMCameras.Mutation setLinkRecord: any;
	@SMCameras.Mutation setWaitingForEvent: any;
	@SMCameras.Mutation setAwaitingCamera: any;
	@SMCameras.Mutation setAwaitingClip: any;

	@SessionStore.Action updateSession: any;
	@SessionStore.Action clearSessions: any;

	@Tours.Getter tourInProgress: boolean;

	private updateTimer: NodeJS.Timeout | null = null;
	private loadedEventId: number = 0;
	private loadedMatrix: boolean = false;
	private showMenu: boolean = true;

	private readonly defaultMediaMatrixLayout: MediaMatrixLayout = SystemDefaultMediaMatrixLayout;
	private eventAreaMediaMatrixLayout: MediaMatrixLayout = this. defaultMediaMatrixLayout;

	/** Computed Props */

	private get isClearEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "Clear"]);
	}

	private get featureHideMenuEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "HideMenu"]);
	}

	private get hideMenuDefault(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "HideMenu", "CollapseByDefault"]);
	}

	private get isMediaMatrixLayoutSetupEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "DefaultMediaMatrixLayout"]);
	}

	private get displayMediaMatrixContent(): boolean {
		return !!this.eventDetails && !this.eventDetails.eventClosed
	}

	private collapseAllSections(): void {
		this.deviceController?.collapseAllSections();
	}

	private get anySectionExpanded(): boolean {
		return this.deviceController?.anySectionExpanded || false;
	}

	/** Methods */

	private async getAreaMediaMatrixLayout(): Promise<void> {
		try {
			const layout: MediaMatrixLayout  = await organizationApi.getMediaMatrixLayout(this.eventDetails.groupID);

			if (layout) {
				this.eventAreaMediaMatrixLayout = layout;
			} 
		} catch (error) {
			console.error('Failed to get media matrix layout:', error);
		}
	}

	private async clearAllMatrixCells(): Promise<void>  {
		var matrix = this.getAllMatrixContents;
		matrix = Object.entries(matrix);

		matrix.forEach(cell => {
			this.setPushContents({
				index: cell[0],
				newContents: {},
				eventID: this.eventId
			});
		});

		const auditRecord = {
			eventId: this.eventId,
			eventRecordTypeId: 4,
			details: "Media Matrix Cleared"
		}

		await this.createAuditRecord(auditRecord);
	}

	public get layoutIndex(): number {
		return this.getLayoutIndex;
	}

	public get layout(): number[] {
		return this.layouts[this.layoutIndex];
	}

	public get layoutHorizontal(): number {
		return this.layouts[this.layoutIndex][0];
	}

	public get layoutVertical(): number {
		return this.layouts[this.layoutIndex][1];
	}

	public get cameraRowStyle(): any {
		return {
			display: "grid",
			"grid-template-rows": 1 / this.layoutHorizontal,
			"grid-template-columns": 1 / this.layoutVertical
		};
	}

	public get eventId(): number {
		return this.eventDetails ? this.eventDetails.eventID : 0;
	}

	public created(): void {
		this.clearMatrixContents();
		this.setRequiresEventDetails(true);
		this.setMediaMatrixIsNew(false);
	}

	private handleEscapeKey(event: KeyboardEvent): void {
		if (event.key === "Escape") {
			if (this.awaitingCamera) {
				this.cancelCameraSelection();
			} else if (this.awaitingClip) {
				this.cancelClipSelection();
			}
		}
	}

	private cancelCameraSelection(): void {
		this.setAwaitingCamera(null);
	}

	private cancelClipSelection(): void {
		this.setAwaitingClip(null);
	}

	public mounted(): void {
		if(this.hideMenuDefault) {
			this.showMenu = false;
		}

		this.loadEvent(this.eventId);
		this.updateTimer = setInterval(() => this.update(), 2000);

		window.addEventListener("keydown", this.handleEscapeKey);
	}

	public destroyed(): void {
		if (this.updateTimer != null) {
			clearInterval(this.updateTimer);
		}
		this.setLinkRecord(null);

		window.removeEventListener("keydown", this.handleEscapeKey);
	}

	/** Watchers */

	// Using "deep" to ensure all updates to matrixContents are seen.
	@Watch("getAllMatrixContents", {deep: true})
	public onMatrixUpdated(matrixContents: any): void {
		let clipsLoaded: Array<string> = [];

		for (let i in matrixContents) {
			if (matrixContents[i].clip !== undefined) {
				let clipIdentifier = matrixContents[i].clip.clip.UniqueFileIdentifier;
				clipsLoaded.push(clipIdentifier);
			}
		}

		this.setShownAuditClips(clipsLoaded);
	}

	@Watch("isController")
	private async onIsControllerChanged(value: boolean, oldValue: boolean): Promise<void> {
		if (value && this.eventId > 0 && this.loadedMatrix) {
			await this.updateMatrix(this.eventId);
		}
	}

	@Watch("eventId")
	public onEventChanged(eventId: number): void {
		this.setWaitingForEvent(eventId == 0);

		this.clearMatrixContents();
		this.setMediaMatrixIsNew(false);
		this.loadEvent(this.eventId);
	}

	private async updateMatrix(eventId: number): Promise<void> {
		this.setMediaMatrixIsNew(false);
		this.setFetchingMatrix(true);

		// get the layout from DB
		const matrix: Matrix = await this.fetchMatrixForEvent(eventId);

		if (matrix) {
			if (this.isController) {
				// Check if any cell has non-empty data, if so, consider new.
				const allBlank = !matrix.cells.some(
					cell =>
						cell.contents !== "{}" &&
						cell.contents !== '{"clip":null,"camera":null}'
				);
				const isNew = matrix.cells.length == 0 || allBlank;

				// Mark for default setup if has no cells or all cells are empty.
				this.setMediaMatrixIsNew(isNew);
				if (!isNew) {
					console.log("Got existing matrix ");
					this.setMatrix(matrix);
				} else {
					console.log("Empty matrix");
				}
			} else {
				this.setMediaMatrixIsNew(false);
				this.setMatrix(matrix);
			}
		} else {
			console.log("New matrix for event " + eventId);

			if (this.isController) {
				if (this.isMediaMatrixLayoutSetupEnabled) {
					this.setLayoutIndexForNewEvent();
				}

				let layout = this.layout;										
				// Save new matrix to DB
				await this.saveMatrix({
					eventID: this.eventId,
					matrixColumns: layout[MatrixLayoutIndices.COL_INDEX],
					matrixRows: layout[MatrixLayoutIndices.ROW_INDEX],
					cells: []
				} as Matrix);

				this.setMediaMatrixIsNew(true);
			} else {
				console.log("Not the controller - matrix will not be saved");
			}
			this.loadedMatrix = true;
		}
	}

	private async loadEvent(eventId: number): Promise<void> {
		if (this.loadedEventId == eventId) {
			return;
		}

		this.loadedEventId = eventId;
		this.clearMatrixContents();
		
		if (eventId != undefined && eventId != 0) {
			// set to default system layout
			this.eventAreaMediaMatrixLayout = { 
				...this.defaultMediaMatrixLayout,
				groupId: this.eventDetails.groupID
			};

			// update the eventAreaMediaMatrixLayout
			if (this.isMediaMatrixLayoutSetupEnabled && this.eventDetails.groupID) {
				await this.getAreaMediaMatrixLayout();
			}

			// update our session keys for audit - we pass the baseURL in here because we sometimes encounter a situation where the axios baseURL
			// hasn't been updated to point at our remote region
			await this.updateSession({
				resourceId: SessionResource.AuditServiceSession,
				eventId: eventId
			});
			await this.updateSession({
				resourceId: SessionResource.DeviceServiceSession,
				eventId: eventId
			});

			await this.updateMatrix(eventId);
		} else {
			this.setDeviceControllerCameras([]);
			this.setDeviceControllerClips([]);
			this.setDeviceControllerOutputs([]);
			this.setDeviceControllerAudioDevices([]);

			this.setAwaitingCamera(null);
			this.setAwaitingClip(null);

			this.setLinkRecord(null);

			this.resetBaseUrl();
			this.loadedMatrix = false;
		}
	}

	/**
	 * Determine which layout to be used for the event.
	 * this method is expected to be called when matrix layout is being set up
	 * for a event for the first time.
	 */
	private setLayoutIndexForNewEvent() : void {
		// layoutIndex is the value for the layout from previous event or system default.
		let newLayoutIndex: number = this.layoutIndex;

		// if the area default matrix layout is defined then use default Area layout
		if (!this.eventAreaMediaMatrixLayout.isSystemDefault) {
			newLayoutIndex = this.eventAreaMediaMatrixLayout.layoutIndex;
		} else {
			// area matrix layout is the default system layout.
			// if user has changed the layout, use user layout selection
			if (this.userLastLayoutIndex) {
				newLayoutIndex = this.userLastLayoutIndex;
			} else {
				// matrix will be rendered using the default system layout
				newLayoutIndex = this.eventAreaMediaMatrixLayout.layoutIndex;
			}
		}

		if (newLayoutIndex != this.layoutIndex) {
			this.setLayoutIndex(newLayoutIndex);
		}
	}

	public async setMatrix(matrix: Matrix): Promise<void> {
		console.log("Setting up matrix ... ");
		
		const layoutIndex = this.layouts.findIndex(
			layout =>
				layout[MatrixLayoutIndices.ROW_INDEX] == matrix.matrixRows && layout[MatrixLayoutIndices.COL_INDEX] == matrix.matrixColumns
		);

		if (layoutIndex > -1) {				
			this.setLayoutIndex(layoutIndex);
		}

		if (matrix.cells != null) {

			const cells =  matrix.cells.map(cell =>	JSON.parse(cell.contents));
			const deviceIds = cells.filter(cell => !!cell.camera).map(cell => cell.camera.deviceId);
			await this.fetchDeviceServerDetails(deviceIds);

			matrix.cells.forEach((cell, index) => {
				// Try to parse the cell contents
				let contents = cells[index];

				// If the cell is empty/invalid, don't do anything
				if (!contents) {
					return;
				}

				//Set contents of cell.

				// If camera, try to find camera in list, but if not, open anyway.
				// Do not get new event record.
				// Only send event record if user is controller.

				// If clip try to find in list, but if not, open anyway.
				let currentContents = this.getMatrixContents(cell.cellIndex);

				if (contents.camera) {
					if (
						currentContents == null ||
						currentContents.camera == null ||
						currentContents.camera.objectID != contents.camera.deviceId
					) {
						// Find an existing nearbyCamera or areaCamera
						const nearbyCameraList: any[] = this.cameras.filter(
							camera => camera.objectId == contents.camera.deviceId
						);
						const areaCameraList: any[] = this.areaCameras.filter(
							camera => camera.objectID == contents.camera.deviceId
						);

						const haveNearbyCamera = nearbyCameraList != null && nearbyCameraList.length > 0;
						const haveAreaCamera = areaCameraList != null && areaCameraList.length > 0
						let haveCameras = haveNearbyCamera || haveAreaCamera;

						let camera = null;

						if (haveCameras) {
							camera = haveNearbyCamera ? nearbyCameraList[0] : areaCameraList[0];
						} else {
							camera = {
								objectID: contents.camera.deviceId,
								title: ""
							};
						}

						const objectId = camera.hasOwnProperty('objectID') ? camera.objectID : camera.objectId;

						if (this.isCameraEnabled(objectId)) {
							this.setPushContents({
								index: cell.cellIndex,
								newContents: {
									camera
								},
								eventId: this.eventId
							});
						}
					}
				} else if (contents.clip) {
					if (
						currentContents == null ||
						currentContents.clip == null ||
						currentContents.clip.clip.UniqueFileIdentifier != contents.clip.UniqueFileIdentifier
					) {
						this.setPushContents({
							index: cell.cellIndex,
							newContents: {
								clip: {
									eventRecordID: contents.clip.eventRecordID,
									clip: {
										FileIdentifier: contents.clip.fileIdentifier,
										UniqueFileIdentifier: contents.clip.uniqueFileIdentifier
									}
								}
							},
							eventId: this.eventId
						});
					}
				} else {
					if (
						currentContents != null &&
						(currentContents.camera != null || currentContents.clip != null)
					) {
						this.setPushContents({
							index: cell.cellIndex,
							newContents: {},
							eventId: this.eventId
						});
					}
				}
			});
		}
	}

	public async update(): Promise<void> {
		if (!this.isController && this.eventId != undefined && this.eventId > 0) {
			// get the layout from DB
			let matrix: Matrix = await this.fetchMatrixForEvent(this.eventId);
			if (matrix) {
				this.setMatrix(matrix);
			}
		}
	}

	public async layoutChanged(evt): Promise<void> {
		const newLayoutIndex = parseInt(evt.target.value);

		if (this.eventId > 0) {
			let [matrixColumns, matrixRows] = this.layouts[newLayoutIndex];

			// Save the new matrix layout to DB
			await this.saveMatrix({
				eventID: this.eventId,
				matrixColumns,
				matrixRows,
				cells: []
			} as Matrix);
		}

		if (!this.isMediaMatrixLayoutSetupEnabled) {
			if (this.userLastLayoutIndex) {
				this.setUserLastLayoutIndex(null);
			}
		} else {
			if (newLayoutIndex === MediaMatrixSystemDefaults.LAYOUT_INDEX) {
				this.setUserLastLayoutIndex(null);
			} else {
				this.setUserLastLayoutIndex(newLayoutIndex);
			}
		}

		// layout change will trigger '/Matrix/Cell' updates from MatrixContainer
		// waiting for matrix update to finish first and change layout after that,
		// otherwise increase of size will cause errors during /Matrix/Cell reqs
		this.setLayoutIndex(newLayoutIndex);
	}

	public cameraColStyle(layoutIndex: number): any {
		let hindex = Math.floor(layoutIndex / this.layoutHorizontal) + 1;
		let vindex = (layoutIndex % this.layoutHorizontal) + 1;

		let style = {
			margin: "2px",
			"grid-column-start": vindex,
			"grid-column-end": vindex,
			"grid-row-start": hindex,
			"grid-row-end": hindex
		};

		return style;
	}

	private expandedItem: HTMLElement | null = null;
	private expandParent: HTMLElement | null = null;
	private itemExpanded: boolean = false;

	public expand(matrixElement: HTMLElement): void {
		if (this.itemExpanded) {
			this.itemExpanded = false;
			this.expandParent.appendChild(matrixElement);
			this.expandParent = null;
			this.expandedItem = null;
		} else {
			this.expandedItem = matrixElement;
			this.itemExpanded = true;
			this.expandParent = matrixElement.parentElement;
			this.expandedCamera.appendChild(matrixElement);
		}
	}

	public contract(matrixElement: HTMLElement): void {
		if (this.itemExpanded && this.expandedItem == matrixElement) {
			this.itemExpanded = false;
			if (this.expandParent != null) {
				this.expandParent.appendChild(matrixElement);
				this.expandParent = null;
			}
		}
	}

	private isVisibleCell(indexToCompare: number): boolean {
		if (this.expandedItem && this.expandParent != null && this.expandParent.parentElement != null) {
			const children = Array.from(this.expandParent.parentElement.children);
			const index = children.indexOf(this.expandParent) + 1;
			return indexToCompare == index;
		}
		return true;
	}

	public resetBaseUrl(): void {
		axiosInstance.prototype.resetBackToOriginalBaseUrl();
	}

	@Watch("linkRecord")
	public onLinkRecordChanged(linkRecord: any): void {
		if (linkRecord && this.itemExpanded) {
			// Auto-collapse when entering link mode
			if (this.expandedItem && this.expandParent) {
				this.itemExpanded = false;
				this.expandParent.appendChild(this.expandedItem);
				this.expandParent = null;
				this.expandedItem = null;
			}
		}
	}
}
